import { useState, useRef, useEffect } from 'react';
import { ReactFlowProvider, useReactFlow, useViewport } from '@xyflow/react';
import { useTranslation } from 'react-i18next';
import { Loader2, FolderOpen } from 'lucide-react';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import { Button } from '@/shared/components/ui/button';
import { TreeChart } from '@/shared/components/custom/tree-chart';
import { orgChartNodeTypes } from './OrgChartNodes';
import { OrgChartToolbar } from './OrgChartToolbar';
import { OrgChartStatusBar } from './OrgChartStatusBar';
import { OrganizationUnitEmptyState } from './OrganizationUnitEmptyState';
import { useOrgChartData } from '../hooks/useOrgChartData';
import { useOrgChartProjectsData } from '../hooks/useOrgChartProjectsData';

export type OrgChartView = 'organization' | 'projects';

interface OrgChartProps {
  onCreateArea: () => void;
}

function OrgChartContent({ onCreateArea }: OrgChartProps) {
  const { t } = useTranslation('organizationUnits');
  const [view, setView] = useState<OrgChartView>('organization');
  const [search, setSearch] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const canvasRef = useRef<HTMLDivElement>(null);
  const { fitView, zoomIn, zoomOut } = useReactFlow();
  const { zoom } = useViewport();

  const organizationData = useOrgChartData(search, onCreateArea, view === 'organization');
  const projectsData = useOrgChartProjectsData(search, view === 'projects');
  const {
    nodes: treeDefs,
    isLoading,
    error,
    totalMembers,
  } = view === 'organization' ? organizationData : projectsData;

  useEffect(() => {
    const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, []);

  const handleToggleFullscreen = () => {
    if (!document.fullscreenElement && canvasRef.current) {
      canvasRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const handleExport = async () => {
    if (!canvasRef.current) return;
    setIsExporting(true);
    await Promise.resolve()
      .then(async () => {
        await new Promise<void>((resolve) => {
          fitView({ padding: 0.15, duration: 0 });
          setTimeout(resolve, 100);
        });

        const dataUrl = await toPng(canvasRef.current!, { quality: 0.95 });
        const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: 'a4' });
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const img = new Image();
        img.src = dataUrl;
        await new Promise<void>((resolve) => {
          img.onload = () => resolve();
        });
        const ratio = Math.min(pdfWidth / img.width, pdfHeight / img.height);
        pdf.addImage(dataUrl, 'PNG', 0, 0, img.width * ratio, img.height * ratio);
        pdf.save('organigrama.pdf');
      })
      .finally(() => setIsExporting(false));
  };

  return (
    <div className="flex h-full flex-col" data-testid="org-chart-container">
      <OrgChartToolbar
        activeView={view}
        onViewChange={setView}
        search={search}
        onSearchChange={setSearch}
        isFullscreen={isFullscreen}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onFitView={() => fitView({ padding: 0.2, duration: 300 })}
        onToggleFullscreen={handleToggleFullscreen}
        onExport={handleExport}
        isExporting={isExporting}
      />

      {isLoading ? (
        <div
          className="flex flex-1 items-center justify-center py-16"
          data-testid="org-chart-loading"
        >
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <div
          className="flex flex-1 flex-col items-center gap-4 py-16"
          data-testid="org-chart-error"
        >
          <p className="text-sm text-muted-foreground">{t('errors.loadFailed')}</p>
          <Button variant="link" size="sm" onClick={() => window.location.reload()}>
            {t('actions.retry')}
          </Button>
        </div>
      ) : treeDefs.length === 0 ? (
        <div className="flex flex-1 items-center justify-center py-16">
          {view === 'organization' ? (
            <OrganizationUnitEmptyState onCreateFirst={onCreateArea} />
          ) : (
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="rounded-full bg-muted p-4">
                <FolderOpen className="h-8 w-8 text-muted-foreground" />
              </div>
              <div>
                <h3 className="text-lg font-medium">{t('orgChart.emptyProjects.title')}</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('orgChart.emptyProjects.description')}
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <>
          <div ref={canvasRef} className="relative flex-1 bg-muted/20">
            <TreeChart nodes={treeDefs} nodeTypes={orgChartNodeTypes} />
          </div>

          <OrgChartStatusBar zoom={zoom} nodeCount={treeDefs.length} memberCount={totalMembers} />
        </>
      )}
    </div>
  );
}

export function OrgChart(props: OrgChartProps) {
  return (
    <ReactFlowProvider>
      <OrgChartContent {...props} />
    </ReactFlowProvider>
  );
}
